// components/website/DecorationEnquiryPage.js - Professional Clean UI
import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { dbPush } from '../../config/firebase';
import { useNavigate } from 'react-router-dom';
import { Send, Calendar, User, Phone, Palette, MessageSquare, CheckCircle, Sparkles, Clock, Briefcase, Users, DollarSign, MapPin, Gift, Heart, Building, PartyPopper } from 'lucide-react';
import { toast } from 'react-toastify';

const DecorationEnquiryPage = ({ onClose }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [formData, setFormData] = useState({
    name: user?.name || '',
    email: user?.email || '',
    phone: user?.phone || '',
    eventType: 'wedding',
    eventDate: '',
    eventVenue: '',
    guestCount: '',
    budget: '',
    theme: '',
    requirements: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [focusedField, setFocusedField] = useState(null);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleBack = () => {
    if (onClose) {
      onClose();
    } else {
      navigate(-1);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.phone) {
      toast.error('Please fill all required fields');
      return;
    }

    setSubmitting(true);
    try {
      await dbPush('decoration_enquiries', {
        ...formData,
        userId: user?.$id || user?.id || null,
        userEmail: user?.email,
        createdAt: new Date().toISOString(),
        status: 'pending',
        assignedTo: null,
        assignedAt: null,
        notes: ''
      });
      setSubmitted(true);
      toast.success('Enquiry sent successfully! We will contact you soon.');
    } catch (error) {
      console.error(error);
      toast.error('Failed to send enquiry. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const eventTypes = [
    { value: 'wedding', label: 'Wedding', icon: <Heart size={20} /> },
    { value: 'birthday', label: 'Birthday', icon: <PartyPopper size={20} /> },
    { value: 'anniversary', label: 'Anniversary', icon: <Heart size={20} /> },
    { value: 'engagement', label: 'Engagement', icon: <Heart size={20} /> },
    { value: 'baby_shower', label: 'Baby Shower', icon: <Users size={20} /> },
    { value: 'corporate', label: 'Corporate', icon: <Building size={20} /> },
    { value: 'festival', label: 'Festival', icon: <Calendar size={20} /> },
    { value: 'other', label: 'Other', icon: <Gift size={20} /> }
  ];

  const themeOptions = [
    'Floral', 'Rustic', 'Modern', 'Traditional', 'Bohemian', 
    'Minimalist', 'Luxury', 'Vintage', 'Beach', 'Garden',
    'Royal', 'Fairy Tale', 'Classic', 'Trendy', 'Custom'
  ];

  if (submitted) {
    return (
      <div style={{
        position: 'fixed',
        inset: 0,
        background: '#f8f9fa',
        zIndex: 3000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px'
      }}>
        <div style={{
          background: 'white',
          borderRadius: '16px',
          padding: '40px',
          textAlign: 'center',
          maxWidth: '450px',
          width: '100%',
          boxShadow: '0 4px 20px rgba(0,0,0,0.08)'
        }}>
          <div style={{
            width: '64px',
            height: '64px',
            borderRadius: '32px',
            background: '#dc2626',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 20px'
          }}>
            <CheckCircle size={32} color="white" />
          </div>
          <h2 style={{ fontSize: '22px', fontWeight: 600, color: '#1a1a1a', marginBottom: '12px' }}>
            Enquiry Sent
          </h2>
          <p style={{ color: '#666', fontSize: '14px', marginBottom: '24px', lineHeight: '1.6' }}>
            Thank you for reaching out. Our decoration expert will contact you within 24 hours with a custom quote.
          </p>
          <button
            onClick={handleBack}
            style={{
              width: '100%',
              padding: '12px',
              background: '#dc2626',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: 500,
              cursor: 'pointer'
            }}
          >
            Close
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      position: 'fixed',
      inset: 0,
      background: '#f8f9fa',
      zIndex: 3000,
      overflowY: 'auto'
    }}>
      {/* Header */}
      <div style={{
        background: 'white',
        padding: '16px 24px',
        borderBottom: '1px solid #e9ecef',
        position: 'sticky',
        top: 0,
        zIndex: 10
      }}>
        <div style={{
          maxWidth: '1000px',
          margin: '0 auto',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: '12px'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <button
              onClick={handleBack}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                background: 'rgb(220, 38, 38)',
                fontWeight:'900',
                borderRadius:'8px',
                border: 'solid 1px',
                cursor: 'pointer',
                fontSize: '14px',
                color: '#fff',
                padding: '10px '
              }}
            >
              ← Back
            </button>
            <div>
              <h1 style={{ fontSize: '20px', fontWeight: 600, margin: 0, color: '#1a1a1a' }}>Decoration Enquiry</h1>
              <p style={{ fontSize: '13px', color: '#888', marginTop: '2px' }}>Get a free quote for your event</p>
            </div>
          </div>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            padding: '4px 10px',
            background: 'rgb(220, 38, 38)',
            borderRadius: '6px'
          }}>
            <Sparkles size={14} color="#fff" />
            <span style={{ fontSize: '12px',fontWeight:'900', color: '#fff' }}>Expert Designers</span>
          </div>
        </div>
      </div>

      {/* Form Container */}
      <div style={{
        maxWidth: '1000px',
        margin: '0 auto',
        padding: '30px 24px'
      }}>
        <form onSubmit={handleSubmit}>
          {/* Personal Information Section */}
          <div style={{
            background: 'white',
            borderRadius: '12px',
            padding: '28px',
            marginBottom: '24px',
            border: '1px solid #e9ecef'
          }}>
            <h3 style={{
              fontSize: '16px',
              fontWeight: 600,
              color: '#1a1a1a',
              marginBottom: '24px',
              paddingBottom: '12px',
              borderBottom: '1px solid #e9ecef',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <User size={18} color="#dc2626" />
              Personal Information
            </h3>
            
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)', 
              gap: '20px' 
            }}>
              <div>
                <label style={labelStyle}>Full Name <span style={{ color: '#dc2626' }}>*</span></label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  onFocus={() => setFocusedField('name')}
                  onBlur={() => setFocusedField(null)}
                  placeholder="Enter your full name"
                  style={{ ...inputStyle, borderColor: focusedField === 'name' ? '#dc2626' : '#dee2e6' }}
                />
              </div>
              <div>
                <label style={labelStyle}>Email Address <span style={{ color: '#dc2626' }}>*</span></label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  onFocus={() => setFocusedField('email')}
                  onBlur={() => setFocusedField(null)}
                  placeholder="your@email.com"
                  style={{ ...inputStyle, borderColor: focusedField === 'email' ? '#dc2626' : '#dee2e6' }}
                />
              </div>
              <div>
                <label style={labelStyle}>Phone Number <span style={{ color: '#dc2626' }}>*</span></label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  onFocus={() => setFocusedField('phone')}
                  onBlur={() => setFocusedField(null)}
                  placeholder="10-digit mobile number"
                  maxLength={10}
                  style={{ ...inputStyle, borderColor: focusedField === 'phone' ? '#dc2626' : '#dee2e6' }}
                />
              </div>
              <div>
                <label style={labelStyle}>Event Venue</label>
                <input
                  type="text"
                  name="eventVenue"
                  value={formData.eventVenue}
                  onChange={handleChange}
                  onFocus={() => setFocusedField('eventVenue')}
                  onBlur={() => setFocusedField(null)}
                  placeholder="Venue name / location"
                  style={{ ...inputStyle, borderColor: focusedField === 'eventVenue' ? '#dc2626' : '#dee2e6' }}
                />
              </div>
            </div>
          </div>

          {/* Event Details Section */}
          <div style={{
            background: 'white',
            borderRadius: '12px',
            padding: '28px',
            marginBottom: '24px',
            border: '1px solid #e9ecef'
          }}>
            <h3 style={{
              fontSize: '16px',
              fontWeight: 600,
              color: '#1a1a1a',
              marginBottom: '24px',
              paddingBottom: '12px',
              borderBottom: '1px solid #e9ecef',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <Calendar size={18} color="#dc2626" />
              Event Details
            </h3>
            
            <div style={{ marginBottom: '20px' }}>
              <label style={labelStyle}>Event Type <span style={{ color: '#dc2626' }}>*</span></label>
              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)', 
                gap: '12px' 
              }}>
                {eventTypes.map(event => (
                  <button
                    key={event.value}
                    type="button"
                    onClick={() => setFormData({ ...formData, eventType: event.value })}
                    style={{
                      padding: '12px',
                      borderRadius: '8px',
                      border: `1px solid ${formData.eventType === event.value ? '#dc2626' : '#dee2e6'}`,
                      background: formData.eventType === event.value ? '#fff5f5' : 'white',
                      cursor: 'pointer',
                      textAlign: 'center',
                      transition: 'all 0.2s'
                    }}
                  >
                    <div style={{ 
                      color: formData.eventType === event.value ? '#dc2626' : '#666',
                      marginBottom: '6px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      {event.icon}
                    </div>
                    <div style={{ 
                      fontSize: '13px', 
                      fontWeight: 500, 
                      color: formData.eventType === event.value ? '#dc2626' : '#333' 
                    }}>
                      {event.label}
                    </div>
                  </button>
                ))}
              </div>
            </div>
            
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)', 
              gap: '20px' 
            }}>
              <div>
                <label style={labelStyle}>Event Date</label>
                <input
                  type="date"
                  name="eventDate"
                  value={formData.eventDate}
                  onChange={handleChange}
                  style={inputStyle}
                />
              </div>
              <div>
                <label style={labelStyle}>Guest Count</label>
                <input
                  type="number"
                  name="guestCount"
                  value={formData.guestCount}
                  onChange={handleChange}
                  placeholder="Number of guests"
                  style={inputStyle}
                />
              </div>
              <div>
                <label style={labelStyle}>Budget (₹)</label>
                <input
                  type="number"
                  name="budget"
                  value={formData.budget}
                  onChange={handleChange}
                  placeholder="e.g., 50000"
                  style={inputStyle}
                />
              </div>
            </div>
          </div>

          {/* Theme & Requirements Section */}
          <div style={{
            background: 'white',
            borderRadius: '12px',
            padding: '28px',
            marginBottom: '24px',
            border: '1px solid #e9ecef'
          }}>
            <h3 style={{
              fontSize: '16px',
              fontWeight: 600,
              color: '#1a1a1a',
              marginBottom: '24px',
              paddingBottom: '12px',
              borderBottom: '1px solid #e9ecef',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <Palette size={18} color="#dc2626" />
              Theme & Requirements
            </h3>
            
            <div style={{ marginBottom: '20px' }}>
              <label style={labelStyle}>Preferred Theme</label>
              <div style={{ 
                display: 'flex', 
                flexWrap: 'wrap', 
                gap: '8px', 
                marginBottom: '12px' 
              }}>
                {themeOptions.map(theme => (
                  <button
                    key={theme}
                    type="button"
                    onClick={() => setFormData({ ...formData, theme: theme === formData.theme ? '' : theme })}
                    style={{
                      padding: '6px 14px',
                      borderRadius: '20px',
                      fontSize: '13px',
                      border: `1px solid ${formData.theme === theme ? '#dc2626' : '#dee2e6'}`,
                      background: formData.theme === theme ? '#fff5f5' : 'white',
                      color: formData.theme === theme ? '#dc2626' : '#555',
                      cursor: 'pointer',
                      transition: 'all 0.2s'
                    }}
                  >
                    {theme}
                  </button>
                ))}
              </div>
              <input
                type="text"
                name="theme"
                value={formData.theme}
                onChange={handleChange}
                placeholder="Or type your custom theme"
                style={inputStyle}
              />
            </div>

            <div>
              <label style={labelStyle}>Requirements / Ideas</label>
              <textarea
                name="requirements"
                value={formData.requirements}
                onChange={handleChange}
                rows={5}
                placeholder="Tell us about your decoration ideas, color preferences, theme vision, specific flowers you want..."
                style={{ ...inputStyle, resize: 'vertical' }}
              />
              <p style={{ fontSize: '11px', color: '#999', marginTop: '8px' }}>
                Be as detailed as possible to help us understand your vision
              </p>
            </div>
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={submitting}
            style={{
              width: '100%',
              padding: '14px',
              background: submitting ? '#adb5bd' : '#dc2626',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontSize: '15px',
              fontWeight: 500,
              cursor: submitting ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              transition: 'background 0.2s'
            }}
          >
            {submitting ? (
              <>
                <div className="spinner" style={{ width: '18px', height: '18px', border: '2px solid white', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.6s linear infinite' }} />
                Sending Enquiry...
              </>
            ) : (
              <>
                <Send size={18} /> Submit Decoration Enquiry
              </>
            )}
          </button>

          <p style={{ textAlign: 'center', fontSize: '12px', color: '#999', marginTop: '16px' }}>
            <Clock size={12} style={{ display: 'inline', marginRight: '4px', verticalAlign: 'middle' }} />
            We'll respond within 24 hours with a custom quote
          </p>
        </form>
      </div>

      <style>{`
        .spinner {
          animation: spin 0.6s linear infinite;
        }
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
        input:focus, select:focus, textarea:focus {
          outline: none;
          border-color: #dc2626 !important;
        }
      `}</style>
    </div>
  );
};

const labelStyle = {
  display: 'block',
  fontSize: '13px',
  fontWeight: 500,
  color: '#555',
  marginBottom: '6px'
};

const inputStyle = {
  width: '100%',
  padding: '10px 12px',
  border: '1px solid #dee2e6',
  borderRadius: '6px',
  fontSize: '14px',
  outline: 'none',
  transition: 'all 0.2s',
  fontFamily: 'inherit',
  background: 'white'
};

export default DecorationEnquiryPage;